import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, List, Search, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { PDF_MAX_SCALE, PDF_MIN_SCALE } from '@/constants/pdf';
import { cn } from '@/lib/utils';

interface PdfToolbarProps {
  currentPage: number;
  totalPages: number;
  scale: number;
  hasResults: boolean;
  showResultsSidebar: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onToggleSearch: () => void;
  onToggleResults: () => void;
}

const PdfToolbar: React.FC<PdfToolbarProps> = ({
  currentPage,
  totalPages,
  scale,
  hasResults,
  showResultsSidebar,
  onPrevPage,
  onNextPage,
  onZoomIn,
  onZoomOut,
  onToggleSearch,
  onToggleResults,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card p-2 shadow-sm">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onPrevPage}
          disabled={currentPage <= 1}
          aria-label={t('table.previousPage')}
        >
          <ChevronLeft />
        </Button>
        <span className="revy-figure text-sm">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onNextPage}
          disabled={currentPage >= totalPages}
          aria-label={t('table.nextPage')}
        >
          <ChevronRight />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" aria-label={t('documents.zoomOut')} onClick={onZoomOut} disabled={scale <= PDF_MIN_SCALE}>
          <ZoomOut />
        </Button>
        <span className="revy-figure min-w-[50px] text-center text-sm">
          {Math.round(scale * 100)}%
        </span>
        <Button variant="ghost" size="icon-sm" aria-label={t('documents.zoomIn')} onClick={onZoomIn} disabled={scale >= PDF_MAX_SCALE}>
          <ZoomIn />
        </Button>
      </div>

      <div className="flex items-center gap-1">
        <TooltipIconButton
          size="icon-sm"
          label={t('documents.pdfSearchTooltip')}
          icon={<Search />}
          onClick={onToggleSearch}
        />
        {hasResults && (
          <TooltipIconButton
            size="icon-sm"
            label={t('documents.toggleResultsList')}
            icon={<List />}
            onClick={onToggleResults}
            className={cn(showResultsSidebar && 'text-primary hover:text-primary')}
          />
        )}
      </div>
    </div>
  );
};

export default PdfToolbar;
